import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Check, Phone, Calendar, Clock, MessageSquare, TrendingUp, Zap, ArrowRight, BarChart3, Shield, Sparkles, CheckCircle, Users, Bell, Star, LayoutGrid, Database, Lock, Command, Globe, Sliders, MonitorPlay, FileText, Activity, Settings, Layers } from 'lucide-react';

// Import Assets
import eliseAvatar from '../assets/elise-avatar.png';
import anaImg from '../assets/ana.png';
import andrewImg from '../assets/andrew.png';
import anyaImg from '../assets/anya.png';
import stephanieImg from '../assets/stephanie.png';

const AIReceptionistPageClean = () => {
  const [activeFeature, setActiveFeature] = useState('calls');
  const [activeTab, setActiveTab] = useState('smart-orders');
  const [isAutoRotating, setIsAutoRotating] = useState(true);

  // Audio Demo Logic
  const [playingTrack, setPlayingTrack] = useState(null);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        @keyframes pulse-ring {
          0% { transform: scale(0.8); opacity: 0.5; }
          100% { transform: scale(1.3); opacity: 0; }
        }
        .animate-pulse-ring {
          animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .glass-card {
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.5);
          box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.07);
        }
      `}</style>
      
      {/* Hero Section */}
      <section className="pt-24 pb-20 px-6 relative overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 via-indigo-50/50 to-blue-100/70"></div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="space-y-5">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm text-blue-700 text-xs font-medium border border-blue-200/50">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                <span>Meet Amy, Your AI Receptionist</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-[56px] font-bold leading-tight tracking-tight">
                <span className="text-gray-900">The #1 AI Dental</span><br />
                <span className="text-blue-600">Receptionist</span>
              </h1>
              
              <p className="text-base text-gray-700 leading-relaxed max-w-lg">
                Amy is the only AI dental receptionist in the world that understands complex scheduling rules to book patients into the correct column, time block, and provider every time.
              </p>
              
              <div className="flex flex-wrap gap-3 pt-2">
                <Link to="/book-demo">
                  <button className="px-6 py-2.5 rounded-lg font-medium text-sm transition-all bg-white text-gray-900 hover:bg-gray-50 border border-gray-300 shadow-sm">
                    Get a demo
                  </button>
                </Link>
                <button className="px-6 py-2.5 rounded-lg font-medium text-sm transition-all bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md">
                  See pricing
                </button>
              </div>
            </div>
            
            {/* Right: Interactive AI Visual */}
            <div className="relative flex items-center justify-center h-[500px]">
              {/* Main Profile with Card */}
              <div className="relative">
                {/* Main Image Card with Gradient Glow */}
                <div className="relative p-1 rounded-3xl bg-gradient-to-br from-blue-300 via-blue-400 to-purple-400 shadow-xl">
                  <div className="relative bg-gradient-to-br from-blue-50 to-purple-50 rounded-[22px] overflow-hidden w-72 h-96">
                    <img
                      src="/assets/amy.png"
                      alt="Amy AI Receptionist"
                      className="w-full h-full object-cover opacity-95"
                    />
                  
                  {/* Glowing circle indicator on ear */}
                  <div className="absolute top-32 right-8">
                    <div className="relative">
                      <div className="w-8 h-8 rounded-full bg-cyan-300 animate-pulse"></div>
                      <div className="absolute inset-0 w-8 h-8 rounded-full bg-cyan-200 animate-ping"></div>
                    </div>
                  </div>
                  
                  {/* Audio waveform at bottom */}
                  <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-white/95 backdrop-blur-sm rounded-2xl px-6 py-3 shadow-md border border-gray-200">
                    <div className="flex items-center gap-1">
                      {[4, 8, 6, 10, 5, 9, 4, 7, 11, 6, 8, 5].map((height, i) => (
                        <div 
                          key={i} 
                          className="w-1 bg-blue-500 rounded-full"
                          style={{ height: `${height * 2}px` }}
                        ></div>
                      ))}
                    </div>
                  </div>
                </div>
                </div>
                
                {/* Speech bubble - top left */}
                <div className="absolute -top-4 -left-16 bg-white rounded-2xl p-4 shadow-md w-56 border border-gray-200">
                  <p className="text-xs text-gray-600 leading-relaxed">Hi, I'm Amy. I can see Dr. Smith has an opening next Tuesday at 2pm. Would you like that?</p>
                  {/* Tail */}
                  <div className="absolute bottom-6 right-0 w-3 h-3 bg-white transform rotate-45 translate-x-1.5 border-r border-b border-gray-200"></div>
                </div>
                
                {/* Response Assistant badge - bottom left */}
                <div className="absolute bottom-24 -left-12 bg-white rounded-xl px-3 py-2 shadow-sm border border-gray-200">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5 text-gray-500" />
                    <span className="text-xs font-medium text-gray-600">Complex Scheduling</span>
                  </div>
                </div>
                
                {/* Stats card - top right */}
                <div className="absolute top-4 -right-16 bg-white rounded-xl p-3 shadow-sm border border-gray-200">
                  <div className="flex items-center gap-2">
                    <Users className="w-3.5 h-3.5 text-blue-500" />
                    <span className="text-xs font-medium text-gray-700">14 Agents</span>
                  </div>
                </div>
                
                {/* Circular stats - bottom right */}
                <div className="absolute -bottom-8 -right-20 bg-white rounded-2xl p-4 shadow-md border border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="relative w-16 h-16">
                      {/* Semi-circle graph */}
                      <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="#e5e7eb"
                          strokeWidth="3"
                        />
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831"
                          fill="none"
                          stroke="#3b82f6"
                          strokeWidth="3"
                          strokeDasharray="100, 100"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xs font-bold text-gray-700">100%</span>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 mb-1">
                        <div className="w-2 h-2 rounded-full bg-green-400"></div>
                        <span className="text-xs text-gray-500">Captured</span>
                      </div>
                      <p className="text-xs font-semibold text-gray-700">Call Answer Rate</p>
                      <p className="text-xs text-gray-400">Zero Missed Calls</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section - Text Left, Audio Right (ADDED FROM FEATURE PAGE) */}
      <section className="py-20 px-6 bg-white overflow-hidden border-b border-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Content */}
            <div className="space-y-8 relative z-10">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-600 text-xs font-medium border border-blue-100 mb-4 uppercase tracking-wide">
                  Experience The Difference
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                  Trained on 11,000+ data points for the <span className="text-blue-600">best patient experience</span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Amy is specifically trained for dental workflows. She understands complex scheduling rules, how to word sensitive messages to patients, how to follow HIPAA & TCPA compliance guidelines, and so much more.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed mt-4">
                  She's especially trained to deliver a positive and empathetic experience to your patients to ensure she represents your brand how you want to be represented.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <Link to="/book-demo">
                  <button className="px-8 py-4 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all transform hover:-translate-y-0.5">
                    Book a Demo
                  </button>
                </Link>
                <button className="px-8 py-4 rounded-xl font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all">
                  View Pricing
                </button>
              </div>

            </div>

            {/* Right: Audio Demo Player */}
            <div className="relative">
              {/* Background Decorative Elements */}
              <div className="absolute -top-10 -right-10 w-64 h-64 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
              <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
              
              <div className="relative bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
                {/* Player Header */}
                <div className="bg-[#0f172a] p-8 text-white">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-2xl font-bold mb-2">Audio Demos</h3>
                      <p className="text-gray-400 text-sm">Choose between 15+ AI voices</p>
                    </div>
                    <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm">
                      <div className="flex items-end gap-1 h-5">
                        <div className="w-1 bg-blue-400 rounded-full h-3 animate-pulse"></div>
                        <div className="w-1 bg-blue-400 rounded-full h-5 animate-pulse delay-75"></div>
                        <div className="w-1 bg-blue-400 rounded-full h-2 animate-pulse delay-150"></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Playlist */}
                <div className="p-6 space-y-4">
                  {/* Track 1 */}
                  <div className="group bg-gray-50 hover:bg-blue-50 rounded-xl p-4 transition-all border border-gray-100 hover:border-blue-100 cursor-pointer">
                    <div className="flex items-center gap-4">
                      <button className="w-12 h-12 rounded-full bg-white shadow-sm group-hover:bg-blue-600 group-hover:text-white flex items-center justify-center transition-all text-blue-600">
                        <div className="w-0 h-0 border-l-[6px] border-l-current border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent ml-0.5"></div>
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-bold text-gray-900 group-hover:text-blue-700 transition-colors">New Patient Booking</h4>
                          <span className="text-xs font-mono text-gray-500 bg-white px-2 py-0.5 rounded border border-gray-200">1:45</span>
                        </div>
                        <p className="text-sm text-gray-500 group-hover:text-blue-600/80 transition-colors">Handling a new inquiry and scheduling seamlessly</p>
                      </div>
                    </div>
                  </div>

                  {/* Track 2 */}
                  <div className="group bg-gray-50 hover:bg-blue-50 rounded-xl p-4 transition-all border border-gray-100 hover:border-blue-100 cursor-pointer">
                    <div className="flex items-center gap-4">
                      <button className="w-12 h-12 rounded-full bg-white shadow-sm group-hover:bg-blue-600 group-hover:text-white flex items-center justify-center transition-all text-blue-600">
                        <div className="w-0 h-0 border-l-[6px] border-l-current border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent ml-0.5"></div>
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-bold text-gray-900 group-hover:text-blue-700 transition-colors">Insurance Verification</h4>
                          <span className="text-xs font-mono text-gray-500 bg-white px-2 py-0.5 rounded border border-gray-200">2:12</span>
                        </div>
                        <p className="text-sm text-gray-500 group-hover:text-blue-600/80 transition-colors">Collecting details and verifying coverage rules</p>
                      </div>
                    </div>
                  </div>

                  {/* Track 3 */}
                  <div className="group bg-gray-50 hover:bg-blue-50 rounded-xl p-4 transition-all border border-gray-100 hover:border-blue-100 cursor-pointer">
                    <div className="flex items-center gap-4">
                      <button className="w-12 h-12 rounded-full bg-white shadow-sm group-hover:bg-blue-600 group-hover:text-white flex items-center justify-center transition-all text-blue-600">
                        <div className="w-0 h-0 border-l-[6px] border-l-current border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent ml-0.5"></div>
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-bold text-gray-900 group-hover:text-blue-700 transition-colors">Emergency After-Hours</h4>
                          <span className="text-xs font-mono text-gray-500 bg-white px-2 py-0.5 rounded border border-gray-200">1:58</span>
                        </div>
                        <p className="text-sm text-gray-500 group-hover:text-blue-600/80 transition-colors">Urgent care triage and on-call notification</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Player Footer */}
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center gap-2">
                    <Shield className="w-3.5 h-3.5" />
                    <span>HIPAA Compliant Voice AI</span>
                  </div>
                  <span>High Fidelity Audio</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEW SECTION: The Only AI That Truly Understands Dentistry (VoiceStack Style) */}
      <section className="py-24 px-6 bg-gradient-to-b from-white to-blue-50/30">
        <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-16 max-w-3xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                    The Only AI That Truly <br/>
                    <span className="text-blue-600">Understands Dentistry</span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                    Most AI receptionists are just message takers. Amy is a fully integrated member of your team, trained on complex dental workflows and supported by a full team of specialized AI teammates.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                {/* Card 1: Complex Scheduling Engine (VoiceStack Style) */}
                <div className="flex flex-col">
                    {/* Visual Area - Glassmorphic on Gradient */}
                    <div className="bg-gradient-to-br from-blue-100 via-purple-100 to-blue-50 rounded-[2.5rem] p-8 h-[400px] relative overflow-hidden mb-6 flex items-center justify-center shadow-sm">
                         {/* Background Orbs */}
                         <div className="absolute top-0 right-0 w-64 h-64 bg-blue-300/30 rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/3"></div>
                         <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-300/30 rounded-full blur-3xl transform -translate-x-1/3 translate-y-1/3"></div>
                         
                         {/* Glass Card Container */}
                         <div className="relative w-full max-w-md">
                            {/* Main Active Call Card */}
                            <div className="glass-card rounded-3xl p-6 relative z-20 bg-white/60 backdrop-blur-xl border border-white/60 shadow-xl">
                                <div className="flex items-start justify-between mb-6">
                                    <div className="flex items-center gap-4">
                                        <div className="relative">
                                            <div className="w-14 h-14 rounded-full overflow-hidden ring-4 ring-white shadow-md">
                                                <img src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=1888&auto=format&fit=crop" alt="Patient" className="w-full h-full object-cover" />
                                            </div>
                                            <div className="absolute -bottom-1 -right-1 bg-green-500 w-4 h-4 rounded-full border-2 border-white"></div>
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-900 text-lg">Sarah Jenkins</h3>
                                            <p className="text-sm text-blue-600 font-medium">New Patient</p>
                                        </div>
                                    </div>
                                    <div className="bg-blue-600/10 px-3 py-1 rounded-full">
                                        <span className="text-xs font-bold text-blue-700 flex items-center gap-1">
                                            <Activity className="w-3 h-3" />
                                            On Call
                                        </span>
                                    </div>
                                </div>

                                {/* Logic Visualization */}
                                <div className="space-y-3">
                                    {/* Intent */}
                                    <div className="flex items-center gap-3 animate-float" style={{ animationDelay: '0s' }}>
                                        <div className="w-1.5 h-12 bg-gradient-to-b from-blue-500 to-transparent rounded-full opacity-20"></div>
                                        <div className="bg-white p-2.5 rounded-xl shadow-sm border border-blue-50 flex items-center gap-2 text-xs font-bold text-gray-700">
                                            <Sparkles className="w-3.5 h-3.5 text-blue-500" />
                                            Intent: Schedule Crown
                                        </div>
                                    </div>
                                    
                                    {/* Rules */}
                                    <div className="flex items-center gap-3 pl-4 animate-float" style={{ animationDelay: '1s' }}>
                                        <div className="bg-white p-2.5 rounded-xl shadow-sm border border-purple-50 flex items-center gap-2 text-xs font-bold text-gray-700">
                                            <Database className="w-3.5 h-3.5 text-purple-500" />
                                            Rules: Delta Dental + Dr. Smith
                                        </div>
                                    </div>

                                    {/* Action */}
                                    <div className="flex justify-end pt-2 animate-float" style={{ animationDelay: '2s' }}>
                                        <div className="bg-blue-600 text-white px-4 py-2 rounded-xl shadow-lg shadow-blue-200 flex items-center gap-2 text-sm font-bold transform scale-105">
                                            <CheckCircle className="w-4 h-4" />
                                            Booked: Tue 10:00 AM
                                        </div>
                                    </div>
                                </div>
                            </div>
                         </div>
                    </div>

                    {/* Text Content */}
                    <div>
                        <div className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1.5 flex items-center gap-2">
                             <Calendar className="w-4 h-4" />
                             SCHEDULING ENGINE
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2.5">Complex Scheduling Engine</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                            The only AI trained to handle complex scheduling rules and book into correct time blocks across operatories. It respects your block scheduling, provider preferences, and operatory capabilities.
                        </p>
                    </div>
                </div>

                {/* Card 2: Team of Agents (VoiceStack Style) */}
                <div className="flex flex-col">
                    {/* Visual Area - Glassmorphic on Gradient */}
                    <div className="bg-gradient-to-br from-indigo-100 via-blue-100 to-indigo-50 rounded-[2.5rem] p-8 h-[400px] relative overflow-hidden mb-6 flex items-center justify-center shadow-sm">
                         {/* Background Orbs */}
                         <div className="absolute top-10 left-10 w-40 h-40 bg-purple-300/30 rounded-full blur-3xl"></div>
                         <div className="absolute bottom-10 right-10 w-40 h-40 bg-blue-300/30 rounded-full blur-3xl"></div>
                         
                        <div className="relative w-full max-w-md z-10">
                            {/* Stacked Cards Effect */}
                            <div className="relative h-64 w-full flex flex-col items-center justify-center">
                                
                                {/* Card 3 (Back) */}
                                <div className="absolute top-0 w-[85%] glass-card bg-white/40 p-4 rounded-2xl border border-white/40 shadow-sm transform scale-90 translate-y-4 opacity-60 z-0">
                                    <div className="flex items-center gap-3 opacity-50">
                                        <div className="w-8 h-8 rounded-full bg-indigo-100 p-0.5">
                                            <img src={andrewImg} className="w-full h-full rounded-full object-cover" alt="Andrew" />
                                        </div>
                                        <div className="h-2 bg-indigo-200 rounded w-24"></div>
                                    </div>
                                </div>

                                {/* Card 2 (Middle) */}
                                <div className="absolute top-8 w-[92%] glass-card bg-white/60 p-4 rounded-2xl border border-white/50 shadow-md transform scale-95 translate-y-2 z-10">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-purple-100 p-0.5">
                                                <img src="/assets/amanda.png" className="w-full h-full rounded-full object-cover" alt="Amanda" />
                                            </div>
                                            <div>
                                                <p className="text-xs font-bold text-gray-800">Amanda (Insurance)</p>
                                                <p className="text-xs text-gray-500">Verifying eligibility...</p>
                                            </div>
                                        </div>
                                        <div className="bg-green-100 text-green-700 text-[10px] font-bold px-2 py-0.5 rounded-full">Active</div>
                                    </div>
                                </div>

                                {/* Card 1 (Front - Main) */}
                                <div className="absolute top-20 w-full glass-card bg-white/80 backdrop-blur-xl p-5 rounded-3xl border border-white/70 shadow-xl z-20 transform hover:-translate-y-1 transition-transform duration-300">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 rounded-full bg-blue-100 p-0.5 ring-2 ring-blue-50">
                                                <img src="/assets/amy.png" className="w-full h-full rounded-full object-cover" alt="Amy" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold text-gray-900">Amy (Receptionist)</p>
                                                <p className="text-xs text-blue-600 font-medium">Coordinating Care</p>
                                            </div>
                                        </div>
                                        <div className="bg-blue-100 p-2 rounded-full text-blue-600">
                                            <MessageSquare className="w-4 h-4" />
                                        </div>
                                    </div>
                                    <div className="bg-gray-50/80 rounded-xl p-3 text-xs text-gray-600 leading-relaxed border border-gray-100">
                                        "I've coordinated with Amanda to verify your insurance. Everything looks good for your appointment on Tuesday!"
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Text Content */}
                    <div>
                        <div className="text-xs font-bold text-purple-600 uppercase tracking-wider mb-1.5 flex items-center gap-2">
                             <Users className="w-4 h-4" />
                             MULTI-AGENT SYSTEM
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2.5">Full Team of 14 AI Agents</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                            The only AI that comes with a full team of specialized agents. Amy collaborates with our Insurance Verifier, Schedule Optimizer, and Recall agents to automate your entire office workflow.
                        </p>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* REFINED SECTION: The World's Most Sophisticated Healthcare AI Voice Engine (Bento Grid) */}
      <section className="py-24 px-6 bg-white border-t border-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="mb-16 max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              The World's Most Sophisticated <br />
              <span className="text-blue-600">Dental AI Voice Model</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Built on a foundation of deep integration and granular control, specifically for the needs of modern dental practices.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Card 1 - Granular Scheduling Rules (Span 2) */}
            <div className="md:col-span-2 bg-gray-50 rounded-[2rem] p-8 overflow-hidden relative group border border-gray-100 transition-all hover:shadow-lg">
              <div className="relative z-10 max-w-lg">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm text-blue-600">
                  <Calendar className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Complex Scheduling Engine</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Define granular rules: "Schedule patients with insurance A for procedure B in operatory C with provider D." Amy accurately schedules and reschedules in the correct column, every time.
                </p>
              </div>
              
              {/* Settings / Rules Graphic Overlay */}
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[45%] h-full hidden md:flex items-center justify-center p-6">
                 <div className="relative w-full max-w-xs">
                     {/* Floating Settings Cards with Glassmorphism */}
                     <div className="glass-card bg-white/80 p-4 rounded-xl shadow-lg mb-3 transform translate-x-4 border border-white/50">
                         <div className="flex items-center gap-3 mb-2">
                             <div className="bg-blue-100 p-1.5 rounded-lg"><Clock className="w-3 h-3 text-blue-600" /></div>
                             <span className="text-xs font-bold text-gray-700">Time Block Rules</span>
                         </div>
                         <div className="h-1.5 w-full bg-gray-100 rounded-full mb-1.5 overflow-hidden"><div className="h-full w-3/4 bg-blue-500 rounded-full"></div></div>
                         <div className="h-1.5 w-2/3 bg-gray-100 rounded-full"></div>
                     </div>

                     <div className="glass-card bg-white/90 p-4 rounded-xl shadow-xl z-10 relative border border-white/60">
                         <div className="flex items-center gap-3 mb-2">
                             <div className="bg-purple-100 p-1.5 rounded-lg"><Settings className="w-3 h-3 text-purple-600" /></div>
                             <span className="text-xs font-bold text-gray-700">Provider Preferences</span>
                         </div>
                         <div className="flex flex-wrap gap-1.5">
                             <span className="text-[9px] bg-gray-100 px-2 py-0.5 rounded text-gray-600 border border-gray-200">Dr. Smith</span>
                             <span className="text-[9px] bg-gray-100 px-2 py-0.5 rounded text-gray-600 border border-gray-200">Op 2</span>
                             <span className="text-[9px] bg-purple-50 px-2 py-0.5 rounded text-purple-600 border border-purple-100 font-medium">Crown</span>
                         </div>
                     </div>

                     <div className="glass-card bg-white/80 p-4 rounded-xl shadow-lg mt-3 transform translate-x-4 border border-white/50">
                         <div className="flex items-center gap-3 mb-2">
                             <div className="bg-green-100 p-1.5 rounded-lg"><Layers className="w-3 h-3 text-green-600" /></div>
                             <span className="text-xs font-bold text-gray-700">Concurrency Limit</span>
                         </div>
                         <div className="flex items-center justify-between text-xs text-gray-500">
                             <span>Max 2 Hygiene</span>
                             <span className="font-bold text-green-600">Active</span>
                         </div>
                     </div>
                 </div>
              </div>
              
              <div className="absolute right-0 bottom-0 w-1/2 h-full bg-gradient-to-l from-blue-50/50 to-transparent -z-10"></div>
            </div>

            {/* Card 2 - Deep Integration (Span 1) */}
            <div className="md:col-span-1 bg-white rounded-[2rem] p-8 overflow-hidden relative group border border-gray-100 transition-all hover:shadow-lg">
              <div className="relative z-10">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-6 text-blue-600">
                  <Database className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Seamless Migration</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Seamless migration from all major Practice Management Systems into Asha PMS.
                </p>
                
                {/* Real PMS Logos */}
                <div className="grid grid-cols-2 gap-4 items-center opacity-80">
                    <div className="p-2 bg-gray-50 rounded-lg flex items-center justify-center h-12">
                        <img src="https://framerusercontent.com/images/FK2PxMGUliSNEptVBpWhWGjhW4.png" alt="Dentrix" className="max-h-8 object-contain" />
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg flex items-center justify-center h-12">
                         <img src="https://framerusercontent.com/images/JN5q8qzSCZ7K4LwF01UYdhkXhmI.png" alt="Eaglesoft" className="max-h-8 object-contain" />
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg flex items-center justify-center h-12">
                         <img src="https://framerusercontent.com/images/rlsK5y9oz2tQ5YKXeUw3yHuCHmQ.png" alt="Open Dental" className="max-h-8 object-contain" />
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg flex items-center justify-center h-12">
                         <img src="https://framerusercontent.com/images/4gWMpRVsQK9Ft5gBD0QXiHTlc.png" alt="Curve" className="max-h-8 object-contain" />
                    </div>
                </div>
              </div>
            </div>

            {/* Card 3 - Customization (Span 1) */}
            <div className="md:col-span-1 bg-gray-50 rounded-[2rem] p-8 overflow-hidden relative group border border-gray-100 transition-all hover:shadow-lg">
              <div className="relative z-10">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm text-blue-600">
                  <Sliders className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">120+ Controls</h3>
                <p className="text-gray-600 leading-relaxed">
                  Customize variables and settings to make the AI receptionist sound and act exactly how you want.
                </p>
              </div>
            </div>

            {/* Card 4 - Full Team (Span 1) */}
             <div className="md:col-span-1 bg-gray-50 rounded-[2rem] p-8 overflow-hidden relative group border border-gray-100 transition-all hover:shadow-lg">
              <div className="relative z-10">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm text-purple-600">
                  <Users className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Team of 14 Agents</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Amy communicates with our other 13 agents to automate your entire front and back office.
                </p>
                
                {/* Agents Stack */}
                {/* <div className="flex flex-wrap gap-2 justify-center">
                    <img src="/assets/amanda.png" className="w-10 h-10 rounded-full border-2 border-white shadow-sm" alt="Amanda" />
                    <img src={anaImg} className="w-10 h-10 rounded-full border-2 border-white shadow-sm" alt="Ana" />
                    <img src={andrewImg} className="w-10 h-10 rounded-full border-2 border-white shadow-sm" alt="Andrew" />
                    <img src={anyaImg} className="w-10 h-10 rounded-full border-2 border-white shadow-sm" alt="Anya" />
                    <img src="/assets/leena.png" className="w-10 h-10 rounded-full border-2 border-white shadow-sm" alt="Leena" />
                </div> */}
              </div>
            </div>

            {/* Card 5 - Dashboard (Span 1) */}
             <div className="md:col-span-1 bg-slate-900 rounded-[2rem] p-8 overflow-hidden relative group transition-all hover:shadow-xl text-white">
              <div className="relative z-10">
                <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center mb-6 text-blue-400">
                  <MonitorPlay className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-3">Full Dashboard</h3>
                <p className="text-slate-400 leading-relaxed">
                  Monitor everything with call recordings, transcripts, and summaries in one place.
                </p>
              </div>
               <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#3b82f6_1px,transparent_1px)] [background-size:16px_16px]"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Why AI Beats Humans - Focus on Quality & Compliance */}
      <section className="py-14 px-6 bg-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            
            {/* Left: Content */}
            <div>
              <p className="text-xs text-blue-600 font-bold uppercase tracking-wider mb-2.5">SUPERIOR AI QUALITY</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-3 leading-tight">
                Curve Balls that Would <br/>
                <span className="text-blue-600">Stump Other AI's...</span>
              </h2>

              <p className="text-base text-gray-600 mb-6 leading-relaxed">
                We've trained Amy on over 320 complex edge cases that cause other AI assistants to fail. From tricky scheduling requests to sensitive privacy situations, she handles them with professional judgment.
              </p>

              <div className="space-y-4">

                <div className="flex gap-3.5">
                  <div className="flex-shrink-0 w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center">
                    <Shield className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-1.5">HIPAA & Privacy Traps</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Example: If a neighbor calls to check an appointment time, other AIs might reveal it. Amy knows to verify identity or transfer to staff, preventing HIPAA violations.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3.5">
                  <div className="flex-shrink-0 w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-1.5">Nuance & Interruptions</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Robotic AIs break when interrupted. Amy handles corrections, mid-sentence changes, and heavy accents with the fluidity of a human receptionist.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3.5">
                  <div className="flex-shrink-0 w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center">
                    <Star className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-1.5">Top Rated Patient Experience</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Patients prefer Amy over voicemail or long hold times. She's polite, efficient, and always available.
                    </p>
                  </div>
                </div>
              </div>

            </div>

            {/* Right: Interactive Dashboard Card (Repurposed for Quality) */}
            <div className="relative h-[500px] bg-gray-50/50 rounded-3xl overflow-hidden border border-gray-200 shadow-xl flex items-center justify-center p-8">
              {/* Decorative Background Elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-100/50 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-green-100/50 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
              
              <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden flex flex-col relative z-10 transform transition-transform hover:scale-[1.02] duration-500">
                {/* Header */}
                <div className="bg-white p-4 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="font-semibold text-sm text-gray-700">Live Transcript</span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-green-50 px-2.5 py-1 rounded-full border border-green-100">
                        <Shield className="w-3 h-3 text-green-600" />
                        <span className="text-[10px] font-bold text-green-700 tracking-wide">HIPAA SAFE</span>
                    </div>
                </div>
                
                {/* Transcript Area */}
                <div className="flex-1 p-5 space-y-6 bg-gray-50/30">
                    {/* User Message */}
                    <div className="flex gap-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-xs font-bold text-gray-500">P</div>
                        <div className="space-y-1">
                            <div className="bg-white p-3.5 rounded-2xl rounded-tl-none shadow-sm border border-gray-100 text-sm text-gray-700 leading-relaxed max-w-[240px]">
                                I'd like to schedule an appointment for my wife, she's 35.
                            </div>
                            <span className="text-[10px] text-gray-400 pl-1">10:42 AM</span>
                        </div>
                    </div>

                    {/* AI Message */}
                    <div className="flex gap-4 flex-row-reverse">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden ring-2 ring-blue-50">
                            <img src="/assets/amy.png" alt="Amy" className="w-full h-full object-cover" />
                        </div>
                        <div className="space-y-1">
                            <div className="bg-blue-600 p-3.5 rounded-2xl rounded-tr-none shadow-md text-sm text-white leading-relaxed max-w-[240px]">
                                I'd be happy to help with that. For privacy compliance, does your wife have a moment to come to the phone, or may I have her call us back directly to confirm her details?
                            </div>
                            <div className="flex items-center justify-end gap-1.5 pr-1">
                                <span className="text-[10px] text-gray-400">10:42 AM</span>
                                <CheckCircle className="w-3 h-3 text-blue-500" />
                            </div>
                        </div>
                    </div>
                    
                    {/* System Alert/Insight */}
                    <div className="mx-4 mt-2 bg-yellow-50 rounded-xl p-3 border border-yellow-100 flex items-start gap-3 shadow-sm">
                        <div className="bg-yellow-100 p-1.5 rounded-full flex-shrink-0 mt-0.5">
                            <Lock className="w-3 h-3 text-yellow-600" />
                        </div>
                        <div>
                            <p className="text-xs font-bold text-gray-900 mb-0.5">Privacy Guard Active</p>
                            <p className="text-[10px] text-gray-600 leading-snug">
                                Detected 3rd party scheduling attempt. Authentication required before proceeding.
                            </p>
                        </div>
                    </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Dentina Style */}
      <section className="py-14 px-6 bg-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-blue-600 via-indigo-600 to-blue-700 shadow-xl border border-blue-500/50">
            <div className="grid lg:grid-cols-2 gap-0 items-center">
              {/* Left: Content */}
              <div className="p-10 lg:p-12 relative z-10">
                <p className="text-xs font-bold text-blue-100 tracking-wider mb-3 uppercase">
                  GET STARTED TODAY - IT'S EASY!
                </p>
                
                <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white leading-tight">
                  The average solo dental office grows production by $8k/month.
                </h2>
                
                <p className="text-base text-blue-100 mb-6 leading-relaxed max-w-xl">
                  For the average small practice, we capture $8k in additional revenue monthly. For single dentist offices, we've driven over $18k. We rigorously attribute ROI so you can see exactly which patients we captured.
                </p>
                
                <Link to="/book-demo">
                  <button className="group px-7 py-3 rounded-xl font-bold text-sm transition-all bg-white text-blue-600 hover:bg-blue-50 shadow-lg hover:shadow-xl">
                    <span className="flex items-center gap-2">
                      Book a Demo
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </button>
                </Link>
              </div>

              {/* Right: Graphic Composition */}
              <div className="relative h-full min-h-[400px] flex items-center justify-center p-8 overflow-hidden">
                {/* Background decorative elements */}
                <div className="absolute inset-0">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 border border-white/10 rounded-full"></div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 border border-white/5 rounded-full"></div>
                </div>

                {/* Main Card */}
                <div className="relative bg-white rounded-2xl shadow-xl border border-gray-100 p-6 w-72 transform rotate-2 hover:rotate-0 transition-transform duration-500">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Live Agent</span>
                    </div>
                    <span className="text-xs font-mono text-gray-400">00:42</span>
                  </div>

                  {/* Avatar Area */}
                  <div className="flex flex-col items-center mb-6">
                    <div className="relative mb-3">
                      <div className="w-20 h-20 rounded-full p-1 bg-gradient-to-br from-blue-100 to-indigo-100">
                        <img
                          src="/assets/amy.png"
                          alt="Amy"
                          className="w-full h-full rounded-full object-cover"
                        />
                      </div>
                      <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow-sm">
                        <div className="bg-blue-600 rounded-full p-1">
                          <Phone className="w-3 h-3 text-white" />
                        </div>
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Amy</h3>
                    <p className="text-sm text-blue-600 font-medium">AI Receptionist</p>
                  </div>

                  {/* Waveform Visualization */}
                  <div className="flex items-center justify-center gap-1 h-8 mb-4">
                    {[1, 2, 3, 2, 4, 3, 5, 4, 2, 3, 1, 2].map((h, i) => (
                      <div
                        key={i}
                        className="w-1 bg-blue-500 rounded-full animate-pulse"
                        style={{ height: `${h * 20}%`, animationDelay: `${i * 0.1}s` }}
                      ></div>
                    ))}
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-lg py-2 text-center">
                      <span className="text-xs font-semibold text-gray-600">Scheduling</span>
                    </div>
                    <div className="bg-gray-50 rounded-lg py-2 text-center">
                      <span className="text-xs font-semibold text-gray-600">Q&A</span>
                    </div>
                  </div>
                </div>

                {/* Floating Elements */}
                <div className="absolute top-20 right-10 bg-white p-3 rounded-xl shadow-lg border border-gray-100 transform rotate-6 animate-float">
                  <CheckCircle className="w-5 h-5 text-blue-500" />
                </div>
                <div className="absolute bottom-20 left-10 bg-white p-3 rounded-xl shadow-lg border border-gray-100 transform -rotate-6 animate-float" style={{ animationDelay: '1s' }}>
                  <Calendar className="w-5 h-5 text-indigo-500" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AIReceptionistPageClean;
